import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Shield, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    display_name: "",
    expected_surgery_date: "",
    shipping_address: {
      full_name: "",
      line1: "",
      line2: "",
      city: "",
      state: "",
      postal_code: "",
      country: "USA",
    },
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && typeof user === "object") {
      setForm({
        display_name: user.display_name || "",
        expected_surgery_date: user.expected_surgery_date || "",
        shipping_address: {
          full_name: user.shipping_address?.full_name || user.display_name || "",
          line1: user.shipping_address?.line1 || "",
          line2: user.shipping_address?.line2 || "",
          city: user.shipping_address?.city || "",
          state: user.shipping_address?.state || "",
          postal_code: user.shipping_address?.postal_code || "",
          country: user.shipping_address?.country || "USA",
        },
      });
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.put("/profile", form);
      await refresh();
      toast.success("Saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSubmitting(false);
    }
  };

  const updateAddr = (field, val) =>
    setForm({ ...form, shipping_address: { ...form.shipping_address, [field]: val } });

  return (
    <div className="container-x py-12 lg:py-16" data-testid="settings-page">
      <div className="max-w-2xl">
        <span className="tag-pill">Settings</span>
        <h1 className="mt-3 font-outfit text-3xl lg:text-4xl font-medium text-ink tracking-tight">
          Your profile
        </h1>
        <p className="mt-3 font-manrope text-ink-soft leading-relaxed">
          Manage your name, surgery date, and shipping address. Your address stays private — guests
          never see it, ever.
        </p>
      </div>

      <form onSubmit={save} className="mt-10 grid gap-6 max-w-2xl">
        <div className="card-soft p-6 lg:p-8">
          <h2 className="font-outfit text-xl font-medium text-ink">Profile</h2>
          <div className="mt-5 grid gap-4">
            <div>
              <label className="font-manrope text-sm text-ink mb-1 block">Display name</label>
              <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="input-soft" data-testid="settings-name-input" />
            </div>
            <div>
              <label className="font-manrope text-sm text-ink mb-1 block">Expected surgery date</label>
              <input type="date" value={form.expected_surgery_date || ""} onChange={(e) => setForm({ ...form, expected_surgery_date: e.target.value })} className="input-soft" data-testid="settings-surgery-date" />
              <p className="font-manrope text-xs text-ink-soft mt-1">
                Displayed as "Week of [date]" so guests know the gift timeline.
              </p>
            </div>
          </div>
        </div>

        <div className="card-soft p-6 lg:p-8">
          <div className="flex items-start gap-3">
            <Lock className="h-5 w-5 text-brand mt-1" />
            <div>
              <h2 className="font-outfit text-xl font-medium text-ink">Shipping address</h2>
              <p className="font-manrope text-sm text-ink-soft mt-1">
                Only you can see this. Guests see "ships to owner's private address" at checkout.
              </p>
            </div>
          </div>
          <div className="mt-5 grid gap-4">
            <div>
              <label className="font-manrope text-sm text-ink mb-1 block">Full name</label>
              <input value={form.shipping_address.full_name} onChange={(e) => updateAddr("full_name", e.target.value)} className="input-soft" data-testid="settings-addr-full-name" />
            </div>
            <div>
              <label className="font-manrope text-sm text-ink mb-1 block">Address line 1</label>
              <input value={form.shipping_address.line1} onChange={(e) => updateAddr("line1", e.target.value)} className="input-soft" data-testid="settings-addr-line1" />
            </div>
            <div>
              <label className="font-manrope text-sm text-ink mb-1 block">Address line 2 (optional)</label>
              <input value={form.shipping_address.line2} onChange={(e) => updateAddr("line2", e.target.value)} className="input-soft" data-testid="settings-addr-line2" />
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="font-manrope text-sm text-ink mb-1 block">City</label>
                <input value={form.shipping_address.city} onChange={(e) => updateAddr("city", e.target.value)} className="input-soft" data-testid="settings-addr-city" />
              </div>
              <div>
                <label className="font-manrope text-sm text-ink mb-1 block">State</label>
                <input value={form.shipping_address.state} onChange={(e) => updateAddr("state", e.target.value)} className="input-soft" data-testid="settings-addr-state" />
              </div>
              <div>
                <label className="font-manrope text-sm text-ink mb-1 block">ZIP</label>
                <input value={form.shipping_address.postal_code} onChange={(e) => updateAddr("postal_code", e.target.value)} className="input-soft" data-testid="settings-addr-zip" />
              </div>
            </div>
            <div>
              <label className="font-manrope text-sm text-ink mb-1 block">Country</label>
              <input value={form.shipping_address.country} onChange={(e) => updateAddr("country", e.target.value)} className="input-soft" data-testid="settings-addr-country" />
            </div>
          </div>
          <div className="mt-5 border-t border-[#EBE2E8] pt-4 flex items-start gap-3">
            <Shield className="h-4 w-4 text-brand mt-0.5" />
            <p className="font-manrope text-xs text-ink-soft">
              Hysteregistry never shows your address in a registry page. When someone buys a gift,
              they ship to you directly — we only confirm to them that you have an address saved.
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={submitting} className="btn-primary" data-testid="settings-save-btn">
            {submitting ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
